import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { IimDatepickerModule, IimDateRangeValue } from '@iim-angular/components';
import { Dayjs } from 'dayjs';
import { BaseSiteLayoutModule, DayjsModule } from '@lib/util';
import { LinkOrderDialogStore } from './link-order-dialog.store';
import { IOrderCandidateHeader, IOrderCandidateItem } from '../../types/IOrderCandidate';

export interface LinkOrderDialogData {
    invoiceId: string;
    /** Approval task the editor was opened from; lets its assignee search without an invoice role. */
    taskId?: string | null;
    /**
     * Invoice head on screen in the editor. Given, the candidates follow it instead of the stored
     * invoice - so a supplier picked a moment ago is searchable without saving first.
     */
    header?: IOrderCandidateHeader;
    /** Currently linked order item, if the dialog is opened on an already linked position. */
    currentRelation?: {
        orderId: string;
        /** Numeric order-item id of the link in the edit mode; without it the position identifies the item. */
        orderItemId?: number;
        orderItemPosition?: number | null;
        orderErpNumber?: string | null;
    };
}

export interface LinkOrderDialogResult {
    /** Set when the user resolved the existing link instead of choosing a new position. */
    remove?: boolean;
    relation?: {
        orderId: string;
        orderItemId: number;
        /** Position of the chosen order item, so the caller can show it without another request. */
        orderItemPosition?: number | null;
        /** ERP number of the chosen order; the caller shows it instead of the eProc number when set. */
        orderErpNumber?: string | null;
    };
}

/**
 * Dialog to link one invoice item to one order item (UC_2020, EPROC-12024).
 *
 * The list it searches is narrowed by the backend to the invoice's company, supplier and company
 * code, so everything offered here is linkable — the dialog does not repeat those rules.
 */
@Component({
    selector: 'app-link-order-dialog',
    standalone: true,
    imports: [
        CommonModule,
        BaseSiteLayoutModule,
        DayjsModule,
        MatDialogModule,
        IimDatepickerModule,
        MatCheckbox,
        MatRadioModule,
        ReactiveFormsModule,
        TranslateModule,
    ],
    templateUrl: './link-order-dialog.component.html',
    styleUrls: ['./link-order-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LinkOrderDialogStore],
})
export class LinkOrderDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<LinkOrderDialogComponent, LinkOrderDialogResult>);
    readonly data = inject<LinkOrderDialogData>(MAT_DIALOG_DATA);
    readonly store = inject(LinkOrderDialogStore);

    /** Order number and order date are the two axes the dialog searches on. */
    readonly searchControl = new FormControl('');
    /** The range picker writes into this instance and reports the change through its own event. */
    readonly orderDateRange = new IimDateRangeValue(null, null);
    /** "zeige geschlossene", as in the delivery-receipt dialog: adds delivered, completed and settled orders. */
    readonly includeClosedControl = new FormControl(false, { nonNullable: true });

    readonly hasSelection = computed(() => this.store.selectedItem() !== null);
    readonly isLinked = computed(() => Boolean(this.data.currentRelation));

    constructor() {
        this.store.init(this.data.invoiceId, this.data.header ?? null, this.data.taskId ?? null);
        this.searchControl.valueChanges.subscribe(() => this.applyFilter());
        this.includeClosedControl.valueChanges.subscribe(() => this.applyFilter());
    }

    applyFilter(): void {
        this.store.search({
            term: this.searchControl.value ?? '',
            orderDateFrom: LinkOrderDialogComponent.toIsoDay(this.orderDateRange.from),
            orderDateTo: LinkOrderDialogComponent.toIsoDay(this.orderDateRange.to),
            includeClosed: this.includeClosedControl.value,
        });
    }

    /** Whether the dialog was opened on a position of this order. */
    isCurrentOrder(orderId: string): boolean {
        return this.data.currentRelation?.orderId === orderId;
    }

    isCurrentRelation(orderId: string, item: IOrderCandidateItem): boolean {
        const current = this.data.currentRelation;
        if (!current || current.orderId !== orderId) {
            return false;
        }
        // Prefer the numeric id when the caller has it; fall back to the position, which the detail
        // view carries even when it does not know the order-item id.
        return current.orderItemId != null
            ? current.orderItemId === item.orderItemId
            : current.orderItemPosition === item.position;
    }

    /** Open quantity on the delivery axis; shown so the user can tell a settled position from an open one. */
    openQuantity(item: IOrderCandidateItem): number | null {
        if (item.quantity === null) {
            return null;
        }
        return item.quantity - (item.quantityShipped ?? 0);
    }

    confirm(): void {
        const item = this.store.selectedItem();
        const orderId = this.store.selectedOrderId();
        if (!item || !orderId) {
            return;
        }
        const order = this.store.candidates().find((candidate) => candidate.orderId === orderId);
        this.dialogRef.close({
            relation: {
                orderId,
                orderItemId: item.orderItemId,
                orderItemPosition: item.position,
                orderErpNumber: order?.erpOrderNumber || null,
            },
        });
    }

    removeLink(): void {
        this.dialogRef.close({ remove: true });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    /** The backend filters on whole days; an invalid half-typed date must not narrow the search. */
    private static toIsoDay(date: Dayjs | null): string | null {
        return date?.isValid() ? date.format('YYYY-MM-DD') : null;
    }
}
